import type { OHLCData, TimeFrame } from "../types";

// Aggregation helper function
export const aggregateOHLCData = (
  data: OHLCData[],
  timeframe: TimeFrame
): OHLCData[] => {
  if (timeframe === "1m" || data.length === 0) return data;

  const aggregated: OHLCData[] = [];
  let currentBucket: OHLCData | null = null;

  data.forEach((candle) => {
    const date = new Date(candle.timestamp);
    const bucketTime = new Date(date);

    // Round down to nearest timeframe
    if (timeframe === "5m") {
      bucketTime.setMinutes(Math.floor(date.getMinutes() / 5) * 5);
    } else if (timeframe === "15m") {
      bucketTime.setMinutes(Math.floor(date.getMinutes() / 15) * 15);
    } else if (timeframe === "30m") {
      bucketTime.setMinutes(Math.floor(date.getMinutes() / 30) * 30);
    } else if (timeframe === "1h") {
      bucketTime.setMinutes(0);
    } else if (timeframe === "2h") {
      bucketTime.setHours(Math.floor(date.getHours() / 2) * 2);
      bucketTime.setMinutes(0);
    } else if (timeframe === "4h") {
      bucketTime.setHours(Math.floor(date.getHours() / 4) * 4);
      bucketTime.setMinutes(0);
    } else if (timeframe === "12h") {
      bucketTime.setHours(Math.floor(date.getHours() / 12) * 12);
      bucketTime.setMinutes(0);
    } else if (timeframe === "1d") {
      bucketTime.setHours(0);
      bucketTime.setMinutes(0);
    } else if (timeframe === "1w") {
      // Round down to Monday of the week
      const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Convert Sunday=0 to 6 days back
      bucketTime.setDate(date.getDate() - daysToSubtract);
      bucketTime.setHours(0);
      bucketTime.setMinutes(0);
    } else if (timeframe === "1mn") {
      bucketTime.setDate(1);
      bucketTime.setHours(0);
      bucketTime.setMinutes(0);
    }

    bucketTime.setSeconds(0);
    bucketTime.setMilliseconds(0);

    const bucketTimestamp = bucketTime.getTime();

    if (!currentBucket || currentBucket.timestamp !== bucketTimestamp) {
      // Start new bucket
      if (currentBucket) {
        aggregated.push(currentBucket);
      }
      currentBucket = {
        timestamp: bucketTimestamp,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume,
      };
    } else {
      // Update current bucket
      currentBucket.high = Math.max(currentBucket.high, candle.high);
      currentBucket.low = Math.min(currentBucket.low, candle.low);
      currentBucket.close = candle.close;
      currentBucket.volume += candle.volume;
    }
  });

  // Don't forget the last bucket
  if (currentBucket) {
    aggregated.push(currentBucket);
  }

  return aggregated;
};
